import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

interface Product {
  bomitemidno: number;
  phdcode: string;
  description: string;
  photofilename: string | null;
  has_photo: boolean;
}

interface OrderListItem {
  id: string;
  order_number: string;
  site_name: string;
  created_at: string;
  total_amount: number;
  status: string;
}

interface OrderDetails {
  id: string;
  order_number: string;
  site_name: string;
  created_at: string;
  status: string;
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  total_amount: number;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
  item_count: number;
}

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'terms' | 'users' | 'photos' | 'settings' | 'orders'>('terms');
  const [termsContent, setTermsContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [termsVersion, setTermsVersion] = useState('');
  const [termsLastUpdated, setTermsLastUpdated] = useState('');

  // Photos tab state
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [uploadingProduct, setUploadingProduct] = useState<string | null>(null);
  const [uploadMessage, setUploadMessage] = useState<{ phdcode: string; type: 'success' | 'error'; text: string } | null>(null);
  const pageSize = 20;

  // Settings tab state
  const [settings, setSettings] = useState({
    shipping_minimum_dollars: '',
    shipping_percentage: '',
    convenience_fee_percentage: '',
    minimum_order_amount: '',
    banner_line_1: '',
    banner_line_2: '',
    support_phone_number: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Order Management tab state
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [selectedOrderDetails, setSelectedOrderDetails] = useState<OrderDetails | null>(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderAction, setOrderAction] = useState<'CANCELLED' | 'REFUNDED' | 'RETURNED' | null>(null);
  const [orderUpdating, setOrderUpdating] = useState(false);
  const [orderMessage, setOrderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Check if user is actually admin
  React.useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/');
    }
  }, [user, navigate]);

  // Load current terms and conditions
  React.useEffect(() => {
    const loadTerms = async () => {
      try {
        const response = await api.get('/admin/terms');
        setTermsContent(response.data.content);
        setTermsVersion(response.data.version);
        setTermsLastUpdated(new Date(response.data.updated_at).toLocaleString());
      } catch (error: any) {
        console.error('Failed to load terms:', error);
        setMessage({
          type: 'error',
          text: 'Failed to load current Terms & Conditions'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.is_admin) {
      loadTerms();
    }
  }, [user]);

  // Load products when Photos tab is selected
  React.useEffect(() => {
    if (activeTab === 'photos' && user?.is_admin) {
      loadProducts();
      loadProductsCount();
    }
  }, [activeTab, currentPage, user]);

  // Load settings when Settings tab is selected
  React.useEffect(() => {
    if (activeTab === 'settings' && user?.is_admin) {
      loadSettings();
    }
  }, [activeTab, user]);

  // Load orders when Orders tab is selected
  React.useEffect(() => {
    if (activeTab === 'orders' && user?.is_admin) {
      loadOrders();
    }
  }, [activeTab, user]);

  const loadProducts = async () => {
    setProductsLoading(true);
    try {
      const response = await api.get(`/admin/products?page=${currentPage}&page_size=${pageSize}`);
      setProducts(response.data);
    } catch (error: any) {
      console.error('Failed to load products:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load products'
      });
    } finally {
      setProductsLoading(false);
    }
  };

  const loadProductsCount = async () => {
    try {
      const response = await api.get('/admin/products/count');
      setTotalProducts(response.data.total);
    } catch (error: any) {
      console.error('Failed to load products count:', error);
    }
  };

  const handleFileUpload = async (phdcode: string, file: File) => {
    setUploadingProduct(phdcode);
    setUploadMessage(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/admin/products/${phdcode}/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadMessage({
        phdcode,
        type: 'success',
        text: 'Photo uploaded successfully!'
      });

      // Reload products to show updated photo
      await loadProducts();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setUploadMessage(null);
      }, 3000);

    } catch (error: any) {
      setUploadMessage({
        phdcode,
        type: 'error',
        text: error.response?.data?.detail || 'Failed to upload photo'
      });
    } finally {
      setUploadingProduct(null);
    }
  };

  const handleSaveTerms = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      await api.post('/admin/update-terms', {
        terms_content: termsContent
      });

      // Reload the terms to get updated version and timestamp
      const response = await api.get('/admin/terms');
      setTermsContent(response.data.content);
      setTermsVersion(response.data.version);
      setTermsLastUpdated(new Date(response.data.updated_at).toLocaleString());

      setMessage({
        type: 'success',
        text: 'Terms & Conditions updated successfully! All users will need to re-accept.'
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to update Terms & Conditions'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const loadSettings = async () => {
    setSettingsLoading(true);
    setSettingsMessage(null);
    try {
      const response = await api.get('/settings/grouped');
      setSettings({
        shipping_minimum_dollars: response.data.shipping_minimum_dollars.toString(),
        shipping_percentage: (response.data.shipping_percentage * 100).toString(),
        convenience_fee_percentage: (response.data.convenience_fee_percentage * 100).toString(),
        minimum_order_amount: response.data.minimum_order_amount.toString(),
        banner_line_1: response.data.banner_line_1,
        banner_line_2: response.data.banner_line_2,
        support_phone_number: response.data.support_phone_number
      });
    } catch (error: any) {
      console.error('Failed to load settings:', error);
      setSettingsMessage({
        type: 'error',
        text: 'Failed to load settings'
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setSettingsMessage(null);

    try {
      // Convert percentages back to decimals
      await api.put('/settings/', {
        shipping_minimum_dollars: parseFloat(settings.shipping_minimum_dollars),
        shipping_percentage: parseFloat(settings.shipping_percentage) / 100,
        convenience_fee_percentage: parseFloat(settings.convenience_fee_percentage) / 100,
        minimum_order_amount: parseFloat(settings.minimum_order_amount),
        banner_line_1: settings.banner_line_1,
        banner_line_2: settings.banner_line_2,
        support_phone_number: settings.support_phone_number
      });

      setSettingsMessage({
        type: 'success',
        text: 'Settings updated successfully!'
      });

      // Reload settings to ensure we have the latest values
      await loadSettings();
    } catch (error: any) {
      setSettingsMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to update settings'
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  // Order Management functions
  const loadOrders = async () => {
    setOrdersLoading(true);
    setOrderMessage(null);
    try {
      const response = await api.get('/admin/orders');
      setOrders(response.data);
    } catch (error: any) {
      console.error('Failed to load orders:', error);
      setOrderMessage({
        type: 'error',
        text: 'Failed to load orders'
      });
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleOrderSelect = async (orderId: string) => {
    setSelectedOrderId(orderId);
    setSelectedOrderDetails(null);
    setOrderAction(null);
    setOrderMessage(null);

    if (!orderId) return;

    setOrderDetailsLoading(true);
    try {
      const response = await api.get(`/admin/orders/${orderId}`);
      setSelectedOrderDetails(response.data);
    } catch (error: any) {
      console.error('Failed to load order details:', error);
      setOrderMessage({
        type: 'error',
        text: 'Failed to load order details'
      });
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const handleUpdateOrderStatus = async () => {
    if (!selectedOrderId || !orderAction) return;

    setOrderUpdating(true);
    setOrderMessage(null);

    try {
      const response = await api.put(`/admin/orders/${selectedOrderId}/status`, {
        status: orderAction
      });

      setOrderMessage({
        type: 'success',
        text: response.data.message
      });

      // Reset selection and reload orders
      setSelectedOrderId('');
      setSelectedOrderDetails(null);
      setOrderAction(null);
      await loadOrders();
    } catch (error: any) {
      setOrderMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to update order status'
      });
    } finally {
      setOrderUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const totalPages = Math.ceil(totalProducts / pageSize);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#4D64C1',
        color: 'white',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px' }}>Admin Dashboard</h1>
            <p style={{ margin: '5px 0 0 0', opacity: 0.9, fontSize: '14px' }}>
              Welcome, {user?.first_name || user?.username}
            </p>
          </div>
          <button
            onClick={logout}
            style={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <i className="fas fa-sign-out-alt" style={{ marginRight: '8px' }}></i>
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '40px auto',
        padding: '0 20px'
      }}>
        {/* Tab Navigation */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #e9ecef'
          }}>
            <button
              onClick={() => setActiveTab('terms')}
              style={{
                flex: 1,
                padding: '15px',
                backgroundColor: activeTab === 'terms' ? '#4D64C1' : 'transparent',
                color: activeTab === 'terms' ? 'white' : '#6c757d',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="fas fa-file-contract" style={{ marginRight: '8px' }}></i>
              Terms & Conditions
            </button>
            <button
              onClick={() => setActiveTab('users')}
              style={{
                flex: 1,
                padding: '15px',
                backgroundColor: activeTab === 'users' ? '#4D64C1' : 'transparent',
                color: activeTab === 'users' ? 'white' : '#6c757d',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="fas fa-users" style={{ marginRight: '8px' }}></i>
              User Management
            </button>
            <button
              onClick={() => setActiveTab('photos')}
              style={{
                flex: 1,
                padding: '15px',
                backgroundColor: activeTab === 'photos' ? '#4D64C1' : 'transparent',
                color: activeTab === 'photos' ? 'white' : '#6c757d',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="fas fa-image" style={{ marginRight: '8px' }}></i>
              Product Photos
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              style={{
                flex: 1,
                padding: '15px',
                backgroundColor: activeTab === 'settings' ? '#4D64C1' : 'transparent',
                color: activeTab === 'settings' ? 'white' : '#6c757d',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="fas fa-cog" style={{ marginRight: '8px' }}></i>
              System Settings
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              style={{
                flex: 1,
                padding: '15px',
                backgroundColor: activeTab === 'orders' ? '#4D64C1' : 'transparent',
                color: activeTab === 'orders' ? 'white' : '#6c757d',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="fas fa-shopping-cart" style={{ marginRight: '8px' }}></i>
              Order Management
            </button>
          </div>

          {/* Tab Content */}
          <div style={{ padding: '30px' }}>
            {activeTab === 'terms' && (
              <div>
                <h2 style={{ marginTop: 0, marginBottom: '10px', color: '#2c3e50' }}>
                  Update Terms & Conditions
                </h2>
                {termsVersion && (
                  <div style={{ marginBottom: '15px', color: '#6c757d', fontSize: '14px' }}>
                    <strong>Current Version:</strong> {termsVersion} | <strong>Last Updated:</strong> {termsLastUpdated}
                  </div>
                )}
                <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                  Edit the Terms & Conditions content below. When you save, the version will auto-increment and all users' acceptance will be reset.
                </p>

                {message && activeTab === 'terms' && (
                  <div style={{
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                    color: message.type === 'success' ? '#155724' : '#721c24',
                    border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                  }}>
                    <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'}`}
                       style={{ marginRight: '8px' }}></i>
                    {message.text}
                  </div>
                )}

                {isLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p style={{ marginTop: '10px', color: '#6c757d' }}>Loading current terms...</p>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '500',
                        color: '#2c3e50'
                      }}>
                        Terms & Conditions Content
                      </label>
                      <textarea
                        value={termsContent}
                        onChange={(e) => setTermsContent(e.target.value)}
                        placeholder="Terms and conditions text..."
                        style={{
                          width: '100%',
                          minHeight: '400px',
                          padding: '15px',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontFamily: 'monospace',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    <button
                      onClick={handleSaveTerms}
                      disabled={!termsContent.trim() || isSaving}
                      style={{
                        backgroundColor: (!termsContent.trim() || isSaving) ? '#cccccc' : '#4D64C1',
                        color: 'white',
                        border: 'none',
                        padding: '12px 30px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '500',
                        cursor: (!termsContent.trim() || isSaving) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save" style={{ marginRight: '8px' }}></i>
                          Save & Reset All User Acceptances
                        </>
                      )}
                    </button>
                  </>
                )}

                <div style={{
                  marginTop: '30px',
                  padding: '20px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  color: '#856404'
                }}>
                  <h4 style={{ marginTop: 0, fontSize: '16px' }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                    What happens when you save?
                  </h4>
                  <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                    <li>The Terms & Conditions in the login page will be updated</li>
                    <li>All users' <code>terms_accepted_on</code> field will be set to NULL</li>
                    <li>Users will be prompted to re-accept the new terms on their next login</li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div>
                <h2 style={{ marginTop: 0, marginBottom: '10px', color: '#2c3e50' }}>
                  User Management
                </h2>
                <p style={{ color: '#6c757d' }}>
                  User management features will be added here as needed.
                  You mentioned there's already a way to manage users in the portal.
                </p>
              </div>
            )}

            {activeTab === 'photos' && (
              <div>
                <h2 style={{ marginTop: 0, marginBottom: '10px', color: '#2c3e50' }}>
                  Product Photo Management
                </h2>
                <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                  Upload product photos for items on the ecommerce site. Photos are automatically named with the product's PHD code.
                </p>

                {productsLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p style={{ marginTop: '10px', color: '#6c757d' }}>Loading products...</p>
                  </div>
                ) : (
                  <>
                    {/* Pagination - Top */}
                    {totalPages > 1 && (
                      <div style={{
                        marginBottom: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: currentPage === 1 ? '#e9ecef' : '#4D64C1',
                            color: currentPage === 1 ? '#6c757d' : 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          <i className="fas fa-chevron-left"></i> Previous
                        </button>
                        <span style={{ color: '#495057', fontSize: '14px' }}>
                          Page {currentPage} of {totalPages} ({totalProducts} products)
                        </span>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: currentPage === totalPages ? '#e9ecef' : '#4D64C1',
                            color: currentPage === totalPages ? '#6c757d' : 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Next <i className="fas fa-chevron-right"></i>
                        </button>
                      </div>
                    )}

                    {/* Products Table */}
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: 'white'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>PHD Code</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Description</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#495057' }}>Current Photo</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#495057' }}>Upload</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((product) => (
                            <tr key={product.phdcode} style={{ borderBottom: '1px solid #dee2e6' }}>
                              <td style={{ padding: '12px', fontWeight: '500', color: '#4D64C1' }}>{product.phdcode}</td>
                              <td style={{ padding: '12px', color: '#495057' }}>{product.description}</td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                {product.has_photo ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                    <img
                                      src={`/api/images/ecomm_products/${product.photofilename}`}
                                      alt={product.phdcode}
                                      style={{
                                        width: '80px',
                                        height: '80px',
                                        objectFit: 'contain',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        padding: '4px',
                                        backgroundColor: '#f8f9fa'
                                      }}
                                    />
                                    <span style={{ fontSize: '12px', color: '#6c757d' }}>{product.photofilename}</span>
                                  </div>
                                ) : (
                                  <span style={{ color: '#6c757d', fontSize: '14px' }}>
                                    <i className="fas fa-image" style={{ marginRight: '4px', opacity: 0.5 }}></i>
                                    No photo
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleFileUpload(product.phdcode, file);
                                      }
                                    }}
                                    disabled={uploadingProduct === product.phdcode}
                                    style={{ display: 'none' }}
                                    id={`file-${product.phdcode}`}
                                  />
                                  <label
                                    htmlFor={`file-${product.phdcode}`}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: uploadingProduct === product.phdcode ? '#6c757d' : '#4D64C1',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: uploadingProduct === product.phdcode ? 'not-allowed' : 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      display: 'inline-block'
                                    }}
                                  >
                                    {uploadingProduct === product.phdcode ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Uploading...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-upload" style={{ marginRight: '6px' }}></i>
                                        Choose File
                                      </>
                                    )}
                                  </label>
                                  {uploadMessage?.phdcode === product.phdcode && (
                                    <div style={{
                                      fontSize: '12px',
                                      color: uploadMessage.type === 'success' ? '#28a745' : '#dc3545',
                                      fontWeight: '500'
                                    }}>
                                      <i className={`fas fa-${uploadMessage.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}
                                         style={{ marginRight: '4px' }}></i>
                                      {uploadMessage.text}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination - Bottom */}
                    {totalPages > 1 && (
                      <div style={{
                        marginTop: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: currentPage === 1 ? '#e9ecef' : '#4D64C1',
                            color: currentPage === 1 ? '#6c757d' : 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          <i className="fas fa-chevron-left"></i> Previous
                        </button>
                        <span style={{ color: '#495057', fontSize: '14px' }}>
                          Page {currentPage} of {totalPages} ({totalProducts} products)
                        </span>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: currentPage === totalPages ? '#e9ecef' : '#4D64C1',
                            color: currentPage === totalPages ? '#6c757d' : 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Next <i className="fas fa-chevron-right"></i>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <h2 style={{ marginTop: 0, marginBottom: '10px', color: '#2c3e50' }}>
                  System Settings
                </h2>
                <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                  Configure system-wide settings for shipping, fees, minimum orders, and site content.
                </p>

                {settingsMessage && (
                  <div style={{
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    backgroundColor: settingsMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                    color: settingsMessage.type === 'success' ? '#155724' : '#721c24',
                    border: `1px solid ${settingsMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                  }}>
                    <i className={`fas fa-${settingsMessage.type === 'success' ? 'check-circle' : 'exclamation-triangle'}`}
                       style={{ marginRight: '8px' }}></i>
                    {settingsMessage.text}
                  </div>
                )}

                {settingsLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p style={{ marginTop: '10px', color: '#6c757d' }}>Loading settings...</p>
                  </div>
                ) : (
                  <>
                    {/* Shipping Settings */}
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{ marginTop: 0, fontSize: '18px', color: '#2c3e50', marginBottom: '15px' }}>
                        <i className="fas fa-shipping-fast" style={{ marginRight: '8px' }}></i>
                        Shipping Calculation
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '6px',
                            fontWeight: '500',
                            color: '#495057',
                            fontSize: '14px'
                          }}>
                            Minimum Shipping ($)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={settings.shipping_minimum_dollars}
                            onChange={(e) => setSettings({ ...settings, shipping_minimum_dollars: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                          <small style={{ color: '#6c757d', fontSize: '12px' }}>
                            Minimum shipping charge regardless of order size
                          </small>
                        </div>
                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '6px',
                            fontWeight: '500',
                            color: '#495057',
                            fontSize: '14px'
                          }}>
                            Shipping Percentage (%)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={settings.shipping_percentage}
                            onChange={(e) => setSettings({ ...settings, shipping_percentage: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                          <small style={{ color: '#6c757d', fontSize: '12px' }}>
                            Percentage of subtotal to charge for shipping
                          </small>
                        </div>
                      </div>
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: '#e7f3ff',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#004085'
                      }}>
                        <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                        Shipping cost = max(minimum, subtotal × percentage)
                      </div>
                    </div>

                    {/* Payment Settings */}
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{ marginTop: 0, fontSize: '18px', color: '#2c3e50', marginBottom: '15px' }}>
                        <i className="fas fa-credit-card" style={{ marginRight: '8px' }}></i>
                        Payment Settings
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '6px',
                            fontWeight: '500',
                            color: '#495057',
                            fontSize: '14px'
                          }}>
                            Convenience Fee (%)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={settings.convenience_fee_percentage}
                            onChange={(e) => setSettings({ ...settings, convenience_fee_percentage: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                          <small style={{ color: '#6c757d', fontSize: '12px' }}>
                            Added to credit/debit card payments
                          </small>
                        </div>
                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '6px',
                            fontWeight: '500',
                            color: '#495057',
                            fontSize: '14px'
                          }}>
                            Minimum Order Amount ($)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={settings.minimum_order_amount}
                            onChange={(e) => setSettings({ ...settings, minimum_order_amount: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                          <small style={{ color: '#6c757d', fontSize: '12px' }}>
                            Orders below this amount will be rejected
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* Site Content */}
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{ marginTop: 0, fontSize: '18px', color: '#2c3e50', marginBottom: '15px' }}>
                        <i className="fas fa-align-left" style={{ marginRight: '8px' }}></i>
                        Site Content
                      </h3>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontWeight: '500',
                          color: '#495057',
                          fontSize: '14px'
                        }}>
                          Banner Line 1
                        </label>
                        <input
                          type="text"
                          value={settings.banner_line_1}
                          onChange={(e) => setSettings({ ...settings, banner_line_1: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                          placeholder="Order PureHD hotel room equipment here…"
                        />
                      </div>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontWeight: '500',
                          color: '#495057',
                          fontSize: '14px'
                        }}>
                          Banner Line 2
                        </label>
                        <input
                          type="text"
                          value={settings.banner_line_2}
                          onChange={(e) => setSettings({ ...settings, banner_line_2: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                          placeholder="Set-Top Boxes • Remotes • Cables • Power Supplies"
                        />
                      </div>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontWeight: '500',
                          color: '#495057',
                          fontSize: '14px'
                        }}>
                          Support Phone Number
                        </label>
                        <input
                          type="text"
                          value={settings.support_phone_number}
                          onChange={(e) => setSettings({ ...settings, support_phone_number: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                          placeholder="(888) 787-3431"
                        />
                      </div>
                    </div>

                    {/* Save Button */}
                    <button
                      onClick={handleSaveSettings}
                      disabled={settingsSaving}
                      style={{
                        backgroundColor: settingsSaving ? '#cccccc' : '#4D64C1',
                        color: 'white',
                        border: 'none',
                        padding: '12px 30px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '500',
                        cursor: settingsSaving ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {settingsSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save" style={{ marginRight: '8px' }}></i>
                          Save Settings
                        </>
                      )}
                    </button>

                    <div style={{
                      marginTop: '30px',
                      padding: '20px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '8px',
                      color: '#856404'
                    }}>
                      <h4 style={{ marginTop: 0, fontSize: '16px' }}>
                        <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                        Important Notes
                      </h4>
                      <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                        <li>Shipping and fee changes take effect immediately for new orders</li>
                        <li>Banner text changes are visible to all users upon next page load</li>
                        <li>Minimum order amount applies to all checkout attempts</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <div>
                <h2 style={{ marginTop: 0, marginBottom: '10px', color: '#2c3e50' }}>
                  Order Management
                </h2>
                <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                  Cancel or refund orders. Select an order from the dropdown to view details and update its status.
                </p>

                {orderMessage && (
                  <div style={{
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    backgroundColor: orderMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                    color: orderMessage.type === 'success' ? '#155724' : '#721c24',
                    border: `1px solid ${orderMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                  }}>
                    <i className={`fas fa-${orderMessage.type === 'success' ? 'check-circle' : 'exclamation-triangle'}`}
                       style={{ marginRight: '8px' }}></i>
                    {orderMessage.text}
                  </div>
                )}

                {ordersLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p style={{ marginTop: '10px', color: '#6c757d' }}>Loading orders...</p>
                  </div>
                ) : (
                  <>
                    {/* Order Selection */}
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '500',
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        Select an Order
                      </label>
                      <select
                        value={selectedOrderId}
                        onChange={(e) => handleOrderSelect(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">-- Select an order --</option>
                        {orders.map((order) => (
                          <option key={order.id} value={order.id}>
                            {order.site_name} | {formatDate(order.created_at)} | {order.order_number} | {formatCurrency(order.total_amount)} | {order.status}
                          </option>
                        ))}
                      </select>
                      {orders.length === 0 && (
                        <p style={{ marginTop: '10px', color: '#6c757d', fontSize: '14px' }}>
                          No orders available for management.
                        </p>
                      )}
                    </div>

                    {/* Order Details */}
                    {orderDetailsLoading && (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span style={{ marginLeft: '10px', color: '#6c757d' }}>Loading order details...</span>
                      </div>
                    )}

                    {selectedOrderDetails && !orderDetailsLoading && (
                      <div style={{
                        backgroundColor: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '20px'
                      }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#2c3e50', fontSize: '18px' }}>
                          Order Details: {selectedOrderDetails.order_number}
                        </h3>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                          <div>
                            <strong style={{ color: '#6c757d', fontSize: '12px', textTransform: 'uppercase' }}>Site Name</strong>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>{selectedOrderDetails.site_name}</p>
                          </div>
                          <div>
                            <strong style={{ color: '#6c757d', fontSize: '12px', textTransform: 'uppercase' }}>Order Date</strong>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>{formatDate(selectedOrderDetails.created_at)}</p>
                          </div>
                          <div>
                            <strong style={{ color: '#6c757d', fontSize: '12px', textTransform: 'uppercase' }}>Current Status</strong>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: '#e9ecef',
                                fontWeight: '500'
                              }}>
                                {selectedOrderDetails.status}
                              </span>
                            </p>
                          </div>
                          <div>
                            <strong style={{ color: '#6c757d', fontSize: '12px', textTransform: 'uppercase' }}>Items</strong>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>{selectedOrderDetails.item_count} item(s)</p>
                          </div>
                        </div>

                        <div style={{
                          backgroundColor: '#f8f9fa',
                          padding: '15px',
                          borderRadius: '6px',
                          marginBottom: '20px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span>Subtotal:</span>
                            <span>{formatCurrency(selectedOrderDetails.subtotal)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span>Shipping:</span>
                            <span>{formatCurrency(selectedOrderDetails.shipping_cost)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span>Tax:</span>
                            <span>{formatCurrency(selectedOrderDetails.tax_amount)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600', borderTop: '1px solid #dee2e6', paddingTop: '8px' }}>
                            <span>Total:</span>
                            <span>{formatCurrency(selectedOrderDetails.total_amount)}</span>
                          </div>
                        </div>

                        {selectedOrderDetails.contact_name && (
                          <div style={{ marginBottom: '20px' }}>
                            <strong style={{ color: '#6c757d', fontSize: '12px', textTransform: 'uppercase' }}>Contact Information</strong>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                              {selectedOrderDetails.contact_name}<br />
                              {selectedOrderDetails.contact_email}<br />
                              {selectedOrderDetails.contact_phone}
                            </p>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '20px' }}>
                          <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px', color: '#2c3e50' }}>
                            Update Order Status
                          </h4>

                          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                            <button
                              onClick={() => setOrderAction('CANCELLED')}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: orderAction === 'CANCELLED' ? '#dc3545' : '#f8d7da',
                                color: orderAction === 'CANCELLED' ? 'white' : '#721c24',
                                border: `2px solid ${orderAction === 'CANCELLED' ? '#dc3545' : '#f5c6cb'}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              <i className="fas fa-times-circle" style={{ marginRight: '8px' }}></i>
                              Cancel Order
                            </button>
                            <button
                              onClick={() => setOrderAction('REFUNDED')}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: orderAction === 'REFUNDED' ? '#ffc107' : '#fff3cd',
                                color: orderAction === 'REFUNDED' ? '#000' : '#856404',
                                border: `2px solid ${orderAction === 'REFUNDED' ? '#ffc107' : '#ffeeba'}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              <i className="fas fa-undo" style={{ marginRight: '8px' }}></i>
                              Refund Order
                            </button>
                            <button
                              onClick={() => setOrderAction('RETURNED')}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: orderAction === 'RETURNED' ? '#6f42c1' : '#e9d5ff',
                                color: orderAction === 'RETURNED' ? 'white' : '#6f42c1',
                                border: `2px solid ${orderAction === 'RETURNED' ? '#6f42c1' : '#d8b4fe'}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              <i className="fas fa-box-open" style={{ marginRight: '8px' }}></i>
                              Return Order
                            </button>
                          </div>

                          {orderAction && (
                            <div style={{
                              backgroundColor: orderAction === 'CANCELLED' ? '#f8d7da' : orderAction === 'RETURNED' ? '#e9d5ff' : '#fff3cd',
                              border: `1px solid ${orderAction === 'CANCELLED' ? '#f5c6cb' : orderAction === 'RETURNED' ? '#d8b4fe' : '#ffeeba'}`,
                              borderRadius: '8px',
                              padding: '20px'
                            }}>
                              <p style={{
                                margin: '0 0 15px 0',
                                fontWeight: '500',
                                color: orderAction === 'CANCELLED' ? '#721c24' : orderAction === 'RETURNED' ? '#6f42c1' : '#856404'
                              }}>
                                <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                                Are you sure you want to {orderAction === 'CANCELLED' ? 'cancel' : orderAction === 'RETURNED' ? 'mark as returned' : 'refund'} order {selectedOrderDetails.order_number}?
                              </p>
                              <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                  onClick={handleUpdateOrderStatus}
                                  disabled={orderUpdating}
                                  style={{
                                    padding: '10px 20px',
                                    backgroundColor: orderAction === 'CANCELLED' ? '#dc3545' : orderAction === 'RETURNED' ? '#6f42c1' : '#ffc107',
                                    color: orderAction === 'CANCELLED' || orderAction === 'RETURNED' ? 'white' : '#000',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: orderUpdating ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                  }}
                                >
                                  {orderUpdating ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-check" style={{ marginRight: '8px' }}></i>
                                      Yes, {orderAction === 'CANCELLED' ? 'Cancel' : orderAction === 'RETURNED' ? 'Mark Returned' : 'Refund'} Order
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => setOrderAction(null)}
                                  disabled={orderUpdating}
                                  style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: orderUpdating ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                  }}
                                >
                                  No, Go Back
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
